/**
 * Locale-aware formatters — Phase 6a.7 per spec §15.
 *
 * Display layer only. The backend returns numeric values as full-precision
 * Decimal strings; we never parse them into Number for storage (precision
 * loss). Formatters accept `string | number` and use `Intl.NumberFormat` /
 * `Intl.DateTimeFormat` against the current i18n locale.
 *
 * Non-finite values render as "—" (em-dash) instead of "NaN" / "Infinity",
 * and invalid dates render as "—" instead of "Invalid Date".
 */

import i18n from "@/i18n";

export type SupportedLocale = "tr-TR" | "en-US";

/** Returns the current Intl locale string based on i18n.language. */
export function getCurrentLocale(): SupportedLocale {
  return i18n.language?.startsWith("tr") ? "tr-TR" : "en-US";
}

/** Format number with locale-aware separators, fixed decimals. */
export function formatNumber(value: number | string, decimals = 2): string {
  // Finding F6 — use Number() (strict), not parseFloat() (which accepts
  // trailing junk like "1.5x" → 1.5). This makes formatNumber consistent with
  // formatSigned/isZeroMoney so a malformed payload renders "—" everywhere
  // rather than a truncated number here and "—" there.
  const num = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat(getCurrentLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

/** Format currency. Currency code shown after value (e.g., "303.747,54 TRY"). */
export function formatCurrency(
  value: number | string,
  currencyCode?: string,
  decimals = 2,
): string {
  const formatted = formatNumber(value, decimals);
  return currencyCode ? `${formatted} ${currencyCode}` : formatted;
}

/** Format percent (input is the percentage, e.g. 12 → "12,00 %" in TR). */
export function formatPercent(value: number | string, decimals = 2): string {
  const formatted = formatNumber(value, decimals);
  return `${formatted} %`;
}

/**
 * Audit-paper amount formatting: negative values wrap in parentheses,
 * positive values use `formatCurrency` directly. Used by Phase 6a.12
 * Balance Sheet and Phase 6a.13 Movement tabs. Non-finite inputs return
 * the raw string unchanged (defensive — backend Decimal strings should
 * always parse, but we won't mask a malformed payload behind a "—").
 */
export function formatSigned(amount: string, currency?: string): string {
  const num = Number(amount);
  if (!Number.isFinite(num)) return amount;
  const abs = Math.abs(num);
  const formatted = formatCurrency(abs, currency);
  return num < 0 ? `(${formatted})` : formatted;
}

/** Format date — locale-aware long form (e.g. "9 Mayıs 2026" / "May 9, 2026"). */
export function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(getCurrentLocale(), {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

/**
 * Format datetime — locale-aware (DD.MM.YYYY HH:mm in TR; MM/DD/YYYY HH:mm
 * in EN).
 */
export function formatDateTime(isoDateTime: string): string {
  const date = new Date(isoDateTime);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(getCurrentLocale(), {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/** Format date as short (DD.MM.YYYY in TR; MM/DD/YYYY in EN). */
export function formatDateShort(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(getCurrentLocale(), {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * Strip locale separators from user input and return a JS number.
 * Inverse of formatNumber. Returns null when the input can't be parsed.
 */
export function parseLocaleNumber(
  input: string,
  locale: string = getCurrentLocale(),
): number | null {
  if (!input || typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  let cleaned: string;
  if (locale === "tr-TR") {
    // TR: thousands separator is ".", decimal separator is ","
    cleaned = trimmed.replace(/\./g, "");
    // Finding F5 — a valid number has at most ONE decimal comma. The previous
    // `.replace(",", ".")` (no /g flag) replaced only the first comma, so
    // malformed input like "12,34,56" was silently truncated to 12.34 instead
    // of rejected. More than one comma is malformed → return null.
    if ((cleaned.match(/,/g)?.length ?? 0) > 1) return null;
    cleaned = cleaned.replace(",", ".");
  } else {
    // EN: thousands separator is ",", decimal separator is "."
    cleaned = trimmed.replace(/,/g, "");
    if ((cleaned.match(/\./g)?.length ?? 0) > 1) return null;
  }
  // Finding F5/F6 — use strict Number(), not parseFloat(): parseFloat silently
  // accepts trailing junk ("1.5x" → 1.5), Number() rejects it (→ NaN → null).
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}
